import React, {forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {Checkbox, DialogActions, DialogContent, FormControlLabel, Grid} from "@mui/material";
import {Button} from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getCustomPageInternalUrl, getDashboardInternalUrl} from "../../../constants/appRoutes";
import ConfirmationDialog from "../../confirmation-dialog";
import CustomDialog from "../../custom-dialog";
import CustomDialogTitle from "../../custom-dialog-title";
import MaterialTable from "../../material-table";
import TabPanel from "../../tab-panel";
import ItemContainerConfigurationDialog from "./ItemContainerConfigurationDialog";
import {ItemContainerSettingsFormContext} from "./ItemContainerSettingsFormProvider";
import {useItemContainerSettingsLabels} from "./useItemContainerSettingsLabels";
import {languageSelector, languagesSelector} from "../../../state/app/appSelectors";
import {hubNodesSelector} from "../../../state/hub/hubSelectors";
import {moduleConfigsSelector} from "../../../state/module-configs/moduleConfigsSelectors";
import {
  clearOtherConfigDashboardToShare,
  clearOtherConfigItemContainers,
  closeOtherConfigItemContainer,
  createOtherConfigItemContainer,
  deleteOtherConfigItemContainer,
  fetchOtherConfigDashboardToShare,
  fetchOtherConfigItemContainers,
  hideOtherConfigItemContainer,
  manageShareDashboardOnNodes,
  updateOtherConfigItemContainer
} from "../../../state/other-config/otherConfigActions";
import {otherConfigSelector} from "../../../state/other-config/otherConfigSelectors";
import {userSelector} from "../../../state/user/userSelectors";
import {getI18nObjCustomFilterAndSearch, localizeI18nObj} from "../../../utils/i18n";
import {
  getIsUserSuperadmin,
  getNodePermissionStr,
  PERMISSIONS_KEY_NODE_DASHBOARD_SHARE
} from "../../../utils/permissions";

interface UserItemContainerSettingsForm {
  asDashboard: boolean;
  onNavigate: any;
}

const TableData = ({
  asDashboard,
  isPrivate,
  userId,
  defaultLanguage,
  languages,
  data,
  goToItemContainer,
  setSelectedItemContainerId,
  setToShareItemContainerId,
  setDeleteItemContainerId
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {scenes} = useItemContainerSettingsLabels(asDashboard);
  const moduleConfigss = useSelector(moduleConfigsSelector);
  const itemContainersActions = moduleConfigss.tableActionFactories["item-containers-settings"] || [];

  return (
    <MaterialTable
      idKey={"dashboards"}
      rightActions={
        !asDashboard || isPrivate
          ? [
              {
                id: "user-item-containers-settings-form__table-action__add",
                label: t(scenes.createElement),
                startIcon: <AddIcon />,
                onClick: () =>
                  dispatch(
                    createOtherConfigItemContainer({
                      type: asDashboard ? "Dashboard" : "CustomPage",
                      title: {},
                      item: [],
                      viewIds: [],
                      views: {},
                      filterLevels: {},
                      isPrivate: true,
                      userId: userId,
                      extra: {
                        rowsHeight: {}
                      }
                    })
                  )
              }
            ]
          : null
      }
      columns={[
        {
          field: "title",
          title: t(scenes.table.columns.title),
          render: ({title}) => localizeI18nObj(title, defaultLanguage, languages),
          customFilterAndSearch: getI18nObjCustomFilterAndSearch(defaultLanguage, languages),
          getClassName: () => "user-item-containers-settings-form__dashboard-table-title"
        }
      ]}
      data={data}
      actions={[
        {
          className: "user-item-containers-settings-form__table-action__view",
          icon: <VisibilityIcon />,
          tooltip: t(scenes.table.viewElement),
          onClick: (_, {id}) => goToItemContainer(id)
        },
        ...itemContainersActions.map(getAction => getAction({asDashboard})),
        {
          className: "user-item-containers-settings-form__table-action__edit",
          icon: <EditIcon />,
          tooltip: t(scenes.table.editElement),
          onClick: (_, {id}) => {
            dispatch(updateOtherConfigItemContainer(id, asDashboard ? "dashboard" : "customPages"));
            setSelectedItemContainerId(id);
          }
        },
        asDashboard && isPrivate
          ? {
              className: "user-item-containers-settings-form__table-action__share",
              icon: <GroupIcon />,
              tooltip: t(scenes.table.shareElement),
              onClick: (_, {id}) => {
                dispatch(fetchOtherConfigDashboardToShare(id));
                setToShareItemContainerId(id);
              }
            }
          : null,
        {
          className: "user-item-containers-settings-form__table-action__delete",
          icon: <DeleteIcon />,
          tooltip: t(scenes.table.deleteElement),
          onClick: (_, {id}) => setDeleteItemContainerId(id)
        }
      ]}
      id={undefined}
      additionalToolbarContent={undefined}
      selectedRows={undefined}
      onRowSelection={undefined}
    />
  );
};

const UserItemContainersSettingsForm = ({asDashboard, onNavigate}: UserItemContainerSettingsForm, ref) => {
  const navigate = useNavigate();
  const {scenes} = useItemContainerSettingsLabels(asDashboard);
  const {itemContainer, itemContainers, needItemContainers, dashboardToShare} = useSelector(otherConfigSelector);
  const defaultLanguage = useSelector(languageSelector);
  const languages = useSelector(languagesSelector);
  const nodes = useSelector(hubNodesSelector);
  const dispatch = useDispatch();
  const {setAsDashboard} = useContext(ItemContainerSettingsFormContext);

  const [deleteItemContainerId, setDeleteItemContainerId] = useState<number>(null);
  const [selectedItemContainerId, setSelectedItemContainerId] = useState<number>(null);
  const [dashboardIdToShare, setDashboardIdToShare] = useState<number>(null);
  const [dashboardSharedNodes, setDashboardSharedNodes] = useState(null);
  const [dashboardConfigNodes, setDashboardConfigNodes] = useState([]);
  const user = useSelector(userSelector);
  const {t} = useTranslation();

  const [tabId, setTabId] = useState("private");

  useEffect(() => {
    setAsDashboard(asDashboard);
    dispatch(fetchOtherConfigItemContainers(asDashboard ? "dashboard" : "customPages"));
  }, [asDashboard, dispatch, setAsDashboard]);

  useEffect(() => {
    return () => {
      dispatch(hideOtherConfigItemContainer());
    };
  }, [dispatch]);

  useEffect(() => {
    if (needItemContainers) {
      dispatch(fetchOtherConfigItemContainers(asDashboard ? "dashboard" : "customPages"));
    }
  }, [asDashboard, dispatch, itemContainers, needItemContainers]);

  useEffect(() => {
    if (asDashboard && dashboardToShare && dashboardIdToShare) {
      setDashboardSharedNodes(dashboardToShare.itemContainer.sharedOnNodes || []);
    } else {
      setDashboardSharedNodes(null);
    }
  }, [asDashboard, dashboardToShare, dashboardIdToShare]);

  useEffect(() => {
    if (asDashboard && dashboardToShare && dashboardIdToShare) {
      setDashboardConfigNodes(dashboardToShare.itemContainer.configNodes.map(node => node.nodeId) || []);
    } else {
      setDashboardConfigNodes([]);
    }
  }, [asDashboard, dashboardToShare, dashboardIdToShare]);

  useImperativeHandle(ref, () => ({
    cancel(f) {
      dispatch(clearOtherConfigItemContainers());
      dispatch(closeOtherConfigItemContainer());
      f();
    }
  }));

  const handleItemContainerClose = () => {
    dispatch(hideOtherConfigItemContainer());
    setSelectedItemContainerId(null);
  };

  const handleShareDialogClose = () => {
    setDashboardIdToShare(null);
    dispatch(clearOtherConfigDashboardToShare());
  };

  const handleNodeCheck = (nodeId: number, checked: boolean) => {
    let currentDashboardSharedNodes = _.cloneDeep(dashboardSharedNodes || []);
    if (checked) {
      currentDashboardSharedNodes = [...currentDashboardSharedNodes, nodeId];
    } else {
      currentDashboardSharedNodes = currentDashboardSharedNodes.filter(id => id !== nodeId);
    }
    setDashboardSharedNodes(currentDashboardSharedNodes);
  };

  const submitDashboardShare = (dashboardId: number, availableNodes: any) => {
    dispatch(manageShareDashboardOnNodes(dashboardId, dashboardSharedNodes || [], availableNodes));
    handleShareDialogClose();
  };

  const goToItemContainer = (itemContainerId: string) => {
    if (
      !RegExp(`${`/${asDashboard ? "dashboards" : "pages"}/${itemContainerId}`}$`).test(
        window.location.href.toLowerCase()
      )
    ) {
      asDashboard
        ? navigate(getDashboardInternalUrl(itemContainerId))
        : navigate(getCustomPageInternalUrl(itemContainerId));
    }
    if (onNavigate) onNavigate();
  };

  const privateData = useMemo(() => {
    return asDashboard ? (itemContainers || []).filter(d => d.ownerId === user.userId) : null;
  }, [asDashboard, itemContainers, user]);

  const sharedData = useMemo(() => {
    return asDashboard ? (itemContainers || []).filter(d => d.ownerId !== user.userId) : null;
  }, [asDashboard, itemContainers, user]);

  const availableNodes = useMemo(() => {
    return asDashboard
      ? getIsUserSuperadmin(user)
        ? nodes
        : (nodes || []).filter(node =>
            user.permissions.includes(getNodePermissionStr(PERMISSIONS_KEY_NODE_DASHBOARD_SHARE, node.nodeId))
          )
      : null;
  }, [asDashboard, user, nodes]);

  return (
    itemContainers && (
      <>
        {asDashboard ? (
          <>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={tabId}
              onChange={(_, id) => {
                setTabId(id);
              }}
              sx={{
                heigth: "49px"
              }}
            >
              <Tab
                id="user-itemContainers-settings-form__private-tab"
                value="private"
                label={t("scenes.dashboardsSettings.tabs.private.label")}
              />
              <Tab
                id="user-itemContainers-settings-form__shared-tab"
                value="shared"
                label={t("scenes.dashboardsSettings.tabs.shared.label")}
              />
            </Tabs>
            <TabPanel
              id="user-itemContainers-settings-form__private-tab-panel"
              value="private"
              selected={tabId}
              sx={{height: "calc(100% - 49px)", paddingTop: "8px"}}
            >
              <TableData
                asDashboard
                isPrivate={true}
                userId={user.userId}
                defaultLanguage={defaultLanguage}
                languages={languages}
                data={privateData}
                goToItemContainer={goToItemContainer}
                setSelectedItemContainerId={setSelectedItemContainerId}
                setToShareItemContainerId={setDashboardIdToShare}
                setDeleteItemContainerId={setDeleteItemContainerId}
              />
            </TabPanel>
            <TabPanel
              id="user-itemContainers-settings-form__shared-tab-panel"
              value="shared"
              selected={tabId}
              sx={{height: "calc(100% - 49px)", paddingTop: "8px"}}
            >
              <TableData
                asDashboard
                isPrivate={false}
                userId={user.userId}
                defaultLanguage={defaultLanguage}
                languages={languages}
                data={sharedData}
                goToItemContainer={goToItemContainer}
                setSelectedItemContainerId={setSelectedItemContainerId}
                setToShareItemContainerId={setDashboardIdToShare}
                setDeleteItemContainerId={setDeleteItemContainerId}
              />
            </TabPanel>
          </>
        ) : (
          <TableData
            asDashboard={false}
            isPrivate={false}
            userId={user.userId}
            defaultLanguage={defaultLanguage}
            languages={languages}
            data={itemContainers}
            goToItemContainer={goToItemContainer}
            setSelectedItemContainerId={setSelectedItemContainerId}
            setDeleteItemContainerId={setDeleteItemContainerId}
            setToShareItemContainerId={setDashboardIdToShare}
          />
        )}

        <ConfirmationDialog
          open={deleteItemContainerId !== null}
          onClose={() => setDeleteItemContainerId(null)}
          onConfirm={() => {
            dispatch(deleteOtherConfigItemContainer(deleteItemContainerId, asDashboard ? "dashboard" : "customPages"));
            setDeleteItemContainerId(null);
          }}
          title={t(scenes.deleteElement.title)}
          content={t(scenes.deleteElement.content)}
        />

        {itemContainer && (
          <ItemContainerConfigurationDialog
            onClose={handleItemContainerClose}
            itemContainerId={selectedItemContainerId}
          />
        )}

        <CustomDialog
          id="user-itemContainers-settings-form__share-dialog"
          open={dashboardIdToShare !== null}
          onClose={handleShareDialogClose}
        >
          <CustomDialogTitle onClose={handleShareDialogClose}>
            {t("scenes.dashboardsSettings.modals.share.title")}
          </CustomDialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid key={-1} size={12}>
                {t("scenes.dashboardsSettings.modals.share.content.title")}
              </Grid>
              {(availableNodes ?? []).map(({nodeId, name}, idx) => (
                <Grid key={idx} size={12}>
                  <FormControlLabel
                    label={name}
                    control={
                      <Checkbox
                        id="user-itemContainers-settings-form__share-dialog__checkbox"
                        checked={(dashboardSharedNodes || []).includes(nodeId)}
                        disabled={dashboardConfigNodes.includes(nodeId)}
                        onChange={(_, checked) => {
                          handleNodeCheck(nodeId, checked);
                        }}
                      />
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button id="user-itemContainers-settings-form__share-dialog__cancel-btn" onClick={handleShareDialogClose}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              id="user-itemContainers-settings-form__share-dialog__confirm-btn"
              onClick={() => submitDashboardShare(dashboardIdToShare, availableNodes)}
            >
              {t("commons.confirm.confirm")}
            </Button>
          </DialogActions>
        </CustomDialog>
      </>
    )
  );
};

export default forwardRef(UserItemContainersSettingsForm);
